var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import React, { useState, useEffect, useMemo, useCallback } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useOkapiKy } from "@folio/stripes/core";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { Button, Checkbox, Loading, MessageBanner, Modal, ModalFooter, Select, TextField, } from "@folio/stripes/components";
import { PublisherContactTypeEnum } from "../../types/Api";
import { fetchReminderSettings, patchReminderSettings, } from "../../api/publisherService";
import { useIsNationalLibrary } from "../../hooks/useIsNationalLibrary";
import { readUrgenceErrorCode } from "../../utils/urgenceSkipSummary";
import styles from "./ReminderSettingsDialog.module.css";
import { invalidateDepositRecordViews } from "../../utils/depositRecordQueries";
var OFFSET_MIN = 0;
var OFFSET_MAX = 365;
var GAP_MIN = 1;
var GAP_MAX = 365;
var OFFSET_ERROR_ID = "ui-br-legaldeposits.publishers.reminderSettings.validation.offsetDays";
var SAVE_ERROR_ID = "ui-br-legaldeposits.publishers.reminderSettings.validation.saveFailed";
var GAP_ERROR_ID = "ui-br-legaldeposits.publishers.reminderSettings.validation.minGapDays";
var BACKEND_ERROR_FIELDS = {
    REMINDER_OFFSET_OUT_OF_RANGE: "offset",
    REMINDER_GAP_OUT_OF_RANGE: "gap",
};
var parseDayValue = function (raw, min, max) {
    var trimmed = raw.trim();
    if (!trimmed)
        return { value: null };
    if (!/^\d+$/.test(trimmed))
        return { invalid: true };
    var parsed = Number(trimmed);
    if (parsed < min || parsed > max)
        return { invalid: true };
    return { value: parsed };
};
var isInvalid = function (parsed) {
    return "invalid" in parsed;
};
export var ReminderSettingsDialog = function (_a) {
    var open = _a.open, onClose = _a.onClose, publisherId = _a.publisherId, contacts = _a.contacts;
    var formatMessage = useIntl().formatMessage;
    var ky = useOkapiKy();
    var queryClient = useQueryClient();
    var activeLibraryId = useIsNationalLibrary().activeLibraryId;
    var _b = useState(true), enabled = _b[0], setEnabled = _b[1];
    var _c = useState(""), offsetDays = _c[0], setOffsetDays = _c[1];
    var _d = useState(""), minGapDays = _d[0], setMinGapDays = _d[1];
    var _e = useState(""), selectedContactId = _e[0], setSelectedContactId = _e[1];
    var _f = useState(null), offsetError = _f[0], setOffsetError = _f[1];
    var _g = useState(null), gapError = _g[0], setGapError = _g[1];
    var _h = useState(null), saveError = _h[0], setSaveError = _h[1];
    // the module stores one reminder row per publisher AND library, so the cache must be library-scoped
    var settingsQueryKey = useMemo(function () { return ["reminder-settings", publisherId, activeLibraryId]; }, [publisherId, activeLibraryId]);
    var _j = useQuery(settingsQueryKey, function () { return fetchReminderSettings(ky, publisherId); }, { enabled: open && !!publisherId && !!activeLibraryId }), settings = _j.data, isFetching = _j.isFetching;
    useEffect(function () {
        var _a, _b, _c, _d, _e;
        if (settings) {
            setEnabled(!settings.reminderDisabled);
            setOffsetDays((_b = (_a = settings.reminderOffsetDays) === null || _a === void 0 ? void 0 : _a.toString()) !== null && _b !== void 0 ? _b : "");
            setMinGapDays((_d = (_c = settings.minReminderGapDays) === null || _c === void 0 ? void 0 : _c.toString()) !== null && _d !== void 0 ? _d : "");
            setSelectedContactId((_e = settings.reminderContactId) !== null && _e !== void 0 ? _e : "");
        }
    }, [settings]);
    var emailContacts = useMemo(function () { return contacts.filter(function (c) { return c.type === PublisherContactTypeEnum.EMAIL; }); }, [contacts]);
    var contactOptions = useMemo(function () {
        var defaultOption = {
            value: "",
            label: formatMessage({
                id: "ui-br-legaldeposits.publishers.reminderSettings.contactEmail.default",
            }),
        };
        var mapped = emailContacts.map(function (c) {
            var _a;
            return ({
                value: (_a = c.id) !== null && _a !== void 0 ? _a : "",
                label: "".concat(c.value).concat(c.isPrimary ? " ★" : "").concat(c.info ? " \u2014 ".concat(c.info) : ""),
            });
        });
        return __spreadArray([defaultOption], mapped, true);
    }, [emailContacts, formatMessage]);
    var mutation = useMutation(function (data) {
        return patchReminderSettings(ky, publisherId, data);
    }, {
        onSuccess: function () {
            queryClient.invalidateQueries(settingsQueryKey);
            // a changed offset recomputes both scheduled days on every unsent record of this publisher
            invalidateDepositRecordViews(queryClient);
            onClose();
        },
        onError: function (error) { return __awaiter(void 0, void 0, void 0, function () {
            var code, field;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, readUrgenceErrorCode(error)];
                    case 1:
                        code = _a.sent();
                        field = code ? BACKEND_ERROR_FIELDS[code] : undefined;
                        setSaveError(null);
                        if (field === "offset") {
                            setOffsetError(formatMessage({ id: OFFSET_ERROR_ID }));
                        }
                        else if (field === "gap") {
                            setGapError(formatMessage({ id: GAP_ERROR_ID }));
                        }
                        else {
                            setSaveError(formatMessage({ id: SAVE_ERROR_ID }));
                        }
                        return [2 /*return*/];
                }
            });
        }); },
    });
    var handleSave = useCallback(function () {
        var parsedOffset = parseDayValue(offsetDays, OFFSET_MIN, OFFSET_MAX);
        var parsedGap = parseDayValue(minGapDays, GAP_MIN, GAP_MAX);
        setOffsetError(isInvalid(parsedOffset) ? formatMessage({ id: OFFSET_ERROR_ID }) : null);
        setGapError(isInvalid(parsedGap) ? formatMessage({ id: GAP_ERROR_ID }) : null);
        if (isInvalid(parsedOffset) || isInvalid(parsedGap))
            return;
        mutation.mutate({
            reminderContactId: selectedContactId || null,
            reminderDisabled: !enabled,
            reminderOffsetDays: parsedOffset.value,
            minReminderGapDays: parsedGap.value,
        });
    }, [
        mutation,
        formatMessage,
        selectedContactId,
        enabled,
        offsetDays,
        minGapDays,
    ]);
    var handleToggleEnabled = useCallback(function () { return setEnabled(function (prev) { return !prev; }); }, []);
    var handleOffsetChange = useCallback(function (e) {
        setOffsetDays(e.target.value);
        setOffsetError(null);
    }, []);
    var handleGapChange = useCallback(function (e) {
        setMinGapDays(e.target.value);
        setGapError(null);
    }, []);
    var handleContactChange = useCallback(function (e) {
        return setSelectedContactId(e.target.value);
    }, []);
    var footer = (React.createElement(ModalFooter, null,
        React.createElement(Button, { buttonStyle: "primary", onClick: handleSave, disabled: !settings || isFetching || mutation.isLoading },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.save" })),
        React.createElement(Button, { onClick: onClose },
            React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.button.cancel" }))));
    return (React.createElement(Modal, { open: open, label: formatMessage({
            id: "ui-br-legaldeposits.publishers.reminderSettings.title",
        }), footer: footer, dismissible: true, onClose: onClose }, !settings ? (React.createElement(Loading, null)) : (React.createElement(React.Fragment, null,
        saveError && (React.createElement("div", { className: styles.warning },
            React.createElement(MessageBanner, { type: "error" }, saveError))),
        !settings.libraryRemindersEnabled && (React.createElement("div", { className: styles.warning },
            React.createElement(MessageBanner, { type: "warning" },
                React.createElement(FormattedMessage, { id: "ui-br-legaldeposits.publishers.reminderSettings.libraryDisabled" })))),
        React.createElement(Checkbox, { label: formatMessage({
                id: "ui-br-legaldeposits.publishers.reminderSettings.enabled",
            }), checked: enabled, onChange: handleToggleEnabled }),
        React.createElement(Select, { label: formatMessage({
                id: "ui-br-legaldeposits.publishers.reminderSettings.contactEmail",
            }), dataOptions: contactOptions, value: selectedContactId, onChange: handleContactChange, disabled: !enabled }),
        React.createElement(TextField, { label: formatMessage({
                id: "ui-br-legaldeposits.publishers.reminderSettings.offsetDays",
            }, { default: settings.defaultReminderOffsetDays }), value: offsetDays, onChange: handleOffsetChange, error: offsetError !== null && offsetError !== void 0 ? offsetError : undefined, disabled: !enabled }),
        React.createElement("div", { className: styles.fieldHint }, formatMessage({
            id: "ui-br-legaldeposits.publishers.reminderSettings.emptyUsesDefault",
        }, { default: settings.defaultReminderOffsetDays })),
        React.createElement(TextField, { label: formatMessage({
                id: "ui-br-legaldeposits.publishers.reminderSettings.minGapDays",
            }, { default: settings.defaultMinGapDays }), value: minGapDays, onChange: handleGapChange, error: gapError !== null && gapError !== void 0 ? gapError : undefined, disabled: !enabled }),
        React.createElement("div", { className: styles.fieldHint }, formatMessage({
            id: "ui-br-legaldeposits.publishers.reminderSettings.emptyUsesDefault",
        }, { default: settings.defaultMinGapDays }))))));
};
